import { useParams, useNavigate } from "react-router-dom";
import {
  Box,
  Container,
  Typography,
  Button,
  Breadcrumbs,
  CircularProgress,
} from "@mui/material";
import { blogPosts } from "../data/blog";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import Link from "@mui/material/Link";
import Markdown from "markdown-to-jsx";
import { useState, useEffect } from "react";
import { Highlight, themes } from "prism-react-renderer";

export const markdownStyles = {
  "& h1, & h2, & h3, & h4, & h5, & h6": {
    scrollMarginTop: "80px",
  },
  "& h1": {
    fontSize: { xs: "2rem", md: "2.5rem" },
    fontWeight: 700,
    marginBottom: "1rem",
    marginTop: "1rem",
    color: "secondary.main",
    lineHeight: 1.2,
  },
  "& h2": {
    fontSize: { xs: "1.5rem", md: "2rem" },
    fontWeight: 600,
    marginBottom: "1rem",
    marginTop: "2.5rem",
    color: "text.primary",
    lineHeight: 1.3,
  },
  "& h3": {
    fontSize: { xs: "1.25rem", md: "1.5rem" },
    fontWeight: 600,
    marginBottom: "0.75rem",
    marginTop: "2rem",
    color: "text.primary",
  },
  "& p": {
    fontSize: { xs: "0.875rem", sm: "1rem" },
    lineHeight: 1.8,
    marginBottom: "1.2rem",
    color: "text.secondary",
    hyphens: "auto",
    WebkitHyphens: "auto",
    msHyphens: "auto",
  },
  "& ul, & ol": {
    marginBottom: "1.5rem",
    paddingLeft: "1.5rem",
    "& li": {
      fontSize: { xs: "0.875rem", sm: "1rem" },
      marginBottom: "0.75rem",
      color: "text.secondary",
      lineHeight: 1.6,
      hyphens: "auto",
      WebkitHyphens: "auto",
      msHyphens: "auto",
    },
  },
  "& table": {
    width: "100%",
    marginBottom: "1.5rem",
    borderCollapse: "collapse",
    "& th, & td": {
      padding: "0.75rem",
      borderBottom: "1px solid",
      borderColor: "divider",
    },
    "& th": {
      fontWeight: 600,
      textAlign: "left",
      color: "text.primary",
    },
    "& td": {
      color: "text.secondary",
    },
  },
  "& img": {
    maxWidth: "100%",
    height: "auto",
    borderRadius: 1,
    marginBottom: "1.5rem",
  },
  "& code": {
    fontFamily: "monospace",
    backgroundColor: "action.hover",
    padding: "0.2rem 0.4rem",
    borderRadius: "4px",
    fontSize: { xs: "0.8rem", sm: "0.9em" },
    color: "secondary.main",
    wordBreak: "break-word",
  },
  "& pre": {
    marginBottom: "1.5rem",
    overflow: "auto",
    maxWidth: "100%",
    "& code": {
      padding: 0,
      backgroundColor: "transparent",
      color: "inherit",
      fontSize: { xs: "0.8rem", sm: "0.9rem" },
      lineHeight: { xs: 1.4, sm: 1.6 },
    },
  },
  "& blockquote": {
    borderLeft: "4px solid",
    borderColor: "primary.main",
    paddingLeft: "1.5rem",
    margin: "1.5rem 0",
    fontStyle: "italic",
    "& p": {
      marginBottom: 0,
    },
  },
  "& a": {
    color: "primary.main",
    textDecoration: "none",
    "&:hover": {
      color: "secondary.main",
      textDecoration: "underline",
    },
  },
  "& .prism-code": {
    padding: { xs: "0.75rem", sm: "1rem" },
    borderRadius: "4px",
    fontSize: { xs: "0.8rem", sm: "0.9rem" },
    overflow: "auto",
    WebkitOverflowScrolling: "touch",
  },
};

export const BlogPost = () => {
  const { slug } = useParams();
  const navigate = useNavigate();
  const post = blogPosts.find((post) => post.slug === slug);
  const [content, setContent] = useState("");
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    if (post) {
      if (post.content.startsWith("/")) {
        setIsLoading(true);
        fetch(`${post.content}?t=${new Date().getTime()}`, {
          headers: {
            "Cache-Control": "no-cache",
            Pragma: "no-cache",
          },
        })
          .then((response) => response.text())
          .then((text) => setContent(text))
          .finally(() => setIsLoading(false));
      } else {
        setContent(post.content);
        setIsLoading(false);
      }
    }
  }, [post]);

  if (!post) {
    return (
      <Container>
        <Typography>Post not found</Typography>
      </Container>
    );
  }

  return (
    <Container
      maxWidth="md"
      sx={{
        py: 8,
        minHeight: "100vh",
        display: "flex",
        flexDirection: "column",
      }}
    >
      <Box sx={{ mb: 4 }}>
        <Breadcrumbs sx={{ mb: 2 }}>
          <Link
            component={Button}
            onClick={() => navigate("/")}
            color="inherit"
          >
            Home
          </Link>
          <Link
            component={Button}
            onClick={() => navigate("/blog")}
            color="inherit"
          >
            Blog
          </Link>
          <Typography color="text.primary">{post.title}</Typography>
        </Breadcrumbs>
        <Button
          startIcon={<ArrowBackIcon />}
          onClick={() => navigate("/blog")}
          sx={{ mb: 4 }}
        >
          Back to Blog
        </Button>
      </Box>

      <Typography variant="h2" gutterBottom>
        {post.title}
      </Typography>

      <Box sx={{ mb: 4 }}>
        <Typography variant="caption" color="text.secondary">
          {new Date(post.date + "T00:00:00Z").toLocaleDateString("en-US", {
            timeZone: "UTC",
          })}{" "}
          · {post.readTime} read
        </Typography>
      </Box>

      <Box sx={{ ...markdownStyles, flex: 1 }}>
        <Typography component="div" variant="body2">
          {isLoading ? (
            <Box sx={{ textAlign: "center", py: 4 }}>
              <CircularProgress />
            </Box>
          ) : (
            <Markdown
              options={{
                forceBlock: true,
                forceWrapper: true,
                wrapper: "div",
                overrides: {
                  p: {
                    component: ({ children }) => (
                      <Typography paragraph>{children}</Typography>
                    ),
                  },
                  code: {
                    component: ({ children, className }) => {
                      if (!className) {
                        return <code>{children}</code>;
                      }

                      const language = className.replace("language-", "");
                      return (
                        <Box component="div" sx={{ my: 2 }}>
                          <Highlight
                            theme={themes.okaidia}
                            code={children as string}
                            language={language}
                          >
                            {({
                              className,
                              style,
                              tokens,
                              getLineProps,
                              getTokenProps,
                            }) => (
                              <pre
                                className={className}
                                style={{
                                  ...style,
                                  padding: "1rem",
                                  borderRadius: "4px",
                                }}
                              >
                                {tokens.map((line, i) => (
                                  <div key={i} {...getLineProps({ line })}>
                                    {line.map((token, key) => (
                                      <span
                                        key={key}
                                        {...getTokenProps({ token })}
                                      />
                                    ))}
                                  </div>
                                ))}
                              </pre>
                            )}
                          </Highlight>
                        </Box>
                      );
                    },
                  },
                },
              }}
            >
              {content}
            </Markdown>
          )}
        </Typography>
      </Box>
    </Container>
  );
};
